import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import LeadsMonthlyDashboard from '../components/LeadsMonthlyDashboard'
import { logout } from '../lib/auth'
import { fetchLeads, formatLeadDate, getMonthlyLeadStats } from '../lib/leads'
import type { Lead } from '../types/lead'

const LeadsPage = () => {
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        const data = await fetchLeads()
        if (!cancelled) {
          setLeads(data)
        }
      } catch (err) {
        if (!cancelled) {
          setLeads([])
          setError(err instanceof Error ? err.message : 'Erro ao carregar leads')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [])

  const monthlyStats = useMemo(() => getMonthlyLeadStats(leads), [leads])
  const totalLeads = leads.length
  const totalQuantidade = leads.reduce((sum, lead) => sum + lead.QuantidadeObjetos, 0)

  const handleLogout = () => {
    logout()
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fc] to-white">
      <header className="border-b border-gray-200/80 bg-white/90 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-[#0877c5] to-[#b51625] flex items-center justify-center text-white font-bold text-sm">
              AC
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Art Custom — Leads</h1>
              <p className="text-sm text-gray-500">Painel de leads</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-sm font-medium text-[#0877c5] hover:text-[#b51625] transition-colors"
            >
              ← Voltar ao site
            </Link>
            <Link
              to="/login"
              onClick={handleLogout}
              className="text-sm font-medium text-gray-500 hover:text-[#b51625] transition-colors"
            >
              Sair
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <p className="text-sm font-semibold tracking-widest uppercase text-[#0877c5] mb-2">
            Painel
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Leads</h2>
          <p className="text-gray-500 max-w-xl">
            Lista de contatos interessados captados pelo formulário de orçamento.
          </p>
        </div>

        {loading && (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-4 border-[#0877c5]/30 border-t-[#0877c5] rounded-full animate-spin" />
            <p className="text-gray-500 mt-4">Carregando leads...</p>
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center mb-6">
            <p className="text-red-700 font-medium">{error}</p>
            <Link
              to="/login"
              className="inline-block mt-3 text-sm font-medium text-[#0877c5] hover:underline"
            >
              Fazer login novamente
            </Link>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-w-lg">
              <div className="rounded-xl border border-gray-200/80 bg-white p-5 shadow-sm">
                <p className="text-sm text-gray-500 mb-1">Total de leads</p>
                <p className="text-2xl font-bold text-[#0877c5]">{totalLeads}</p>
              </div>
              <div className="rounded-xl border border-gray-200/80 bg-white p-5 shadow-sm">
                <p className="text-sm text-gray-500 mb-1">Quantidade total (peças)</p>
                <p className="text-2xl font-bold text-[#b51625]">
                  {totalQuantidade.toLocaleString('pt-BR')}
                </p>
              </div>
            </div>

            <LeadsMonthlyDashboard stats={monthlyStats} />

            <div className="rounded-xl border border-gray-200/80 bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 sm:px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Data
                      </th>
                      <th className="px-4 sm:px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Nome
                      </th>
                      <th className="px-4 sm:px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Telefone
                      </th>
                      <th className="px-4 sm:px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        E-mail
                      </th>
                      <th className="px-4 sm:px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                        Quantidade
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leads.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                          Nenhum lead encontrado
                        </td>
                      </tr>
                    ) : (
                      leads.map((lead) => (
                        <tr key={lead.Id} className="hover:bg-[#0877c5]/5 transition-colors">
                          <td className="px-4 sm:px-6 py-4 text-gray-600 whitespace-nowrap text-sm">
                            {formatLeadDate(lead.CreatedAt)}
                          </td>
                          <td className="px-4 sm:px-6 py-4 font-medium text-gray-900">
                            {lead.Nome || '—'}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-gray-600 whitespace-nowrap">
                            {lead.Telefone || '—'}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-gray-600">
                            {lead.Email ? (
                              <a
                                href={`mailto:${lead.Email}`}
                                className="text-[#0877c5] hover:underline break-all"
                              >
                                {lead.Email}
                              </a>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-right font-semibold text-gray-900">
                            {lead.QuantidadeObjetos.toLocaleString('pt-BR')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  )
}

export default LeadsPage
